<script setup lang="ts">
import Datepicker from "@vuepic/vue-datepicker";
import { computed } from "vue";
import { pt, Locale } from "date-fns/locale";
import { useI18n } from "vue-i18n";

import { useField } from "vee-validate";
import type { RuleExpression } from "vee-validate";
const { locale } = useI18n();
const props = withDefaults(
  defineProps<{
    name?: string;
    rules?: RuleExpression<any>;
    title?: string;
    locale?: string;
    formatLocale?: Locale;
    format?: string;
    modelValue?: string | Date | number | null;
    nowButtonLabel?: string;
    cancelText?: string;
    selectText?: string;
    placeholder?: string;
    required?: boolean;
    readonly?: boolean;
  }>(),
  {
    locale: "pt-PT",
    formatLocale: () => pt,
    format: "dd/MM/yyyy HH:mm",
    modelValue: null,
    required: false,
    readonly: false,
  },
);

const emit = defineEmits<{
  "update:modelValue": [value: string | number | null];
}>();

// Only hook into vee-validate when a name is actually given
const field = props.name ? useField(() => props.name!, props.rules) : null;

const value = computed({
  get: () => (field ? field.value.value : props.modelValue),
  set: (val) => {
    if (field) {
      field.value.value = val;
    } else {
      emit("update:modelValue", val);
    }
  },
});

const errorMessage = computed(() => field?.errorMessage.value);

const inputUiClass = computed(() => {
  if (props.readonly) {
    return "bg-gray-100! border! border-gray-300! text-gray-500! cursor-not-allowed!";
  }
  if (errorMessage.value) {
    return "border! border-red-400!";
  }
  return "border! border-gray-300! focus:ring-2! focus:ring-[#28b473]! focus:border-[#28b473]!";
});

const { t } = useI18n();
</script>

<template>
  <div class="flex flex-col gap-1">
    <label class="font-semibold text-gray-800 text-sm">
      {{ title + "" + (required ? "*" : "") }}
    </label>

    <Datepicker
      :ui="{ input: inputUiClass }"
      v-model="value"
      v-bind="$attrs"
      :clearable="true"
      :enable-time-picker="true"
      :locale="locale"
      :format-locale="formatLocale"
      :format="format"
      :placeholder="placeholder ?? t('common.datePicker.placeholder')"
      :select-text="selectText ?? t('common.datePicker.select')"
      :cancel-text="cancelText ?? t('common.datePicker.cancel')"
      :now-button-label="nowButtonLabel ?? t('common.datePicker.nowButton')"
      :readonly="readonly"
      auto-apply
      teleport="body"
    />
    <span v-if="errorMessage" class="text-red-500 text-xs">{{
      errorMessage
    }}</span>
  </div>
</template>
